import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * CelestialWheel - A single scroll wheel column for the tumbler picker
 * iOS-style picker with gold highlighting and transparent background
 */
const CelestialWheel = ({ 
    items, 
    value, 
    onChange, 
    width = "w-20",
    label = ""
}) => {
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const itemHeight = 40; // Height of each item in pixels
    const visibleItems = 5; // Number of visible items
    
    // Find current index
    const currentIndex = items.findIndex(item => item.value === value);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    
    // Calculate scroll position
    const [scrollOffset, setScrollOffset] = useState(0);
    const targetOffset = -safeIndex * itemHeight;
    
    useEffect(() => {
        setScrollOffset(targetOffset);
    }, [targetOffset]);

    // Lock body scroll when dragging
    useEffect(() => {
        if (isDragging) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        } else {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        };
    }, [isDragging]);

    // Add non-passive wheel event listener
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const wheelHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const delta = Math.sign(e.deltaY);
            const newIndex = Math.max(0, Math.min(items.length - 1, safeIndex + delta));
            if (newIndex !== safeIndex) {
                onChange(items[newIndex].value);
            }
        };

        // Use non-passive listener to enable preventDefault
        container.addEventListener('wheel', wheelHandler, { passive: false });
        
        return () => {
            container.removeEventListener('wheel', wheelHandler);
        };
    }, [items, safeIndex, onChange]);

    // Handle wheel scroll (kept for completeness but actual handling is in useEffect)
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = Math.sign(e.deltaY);
        const newIndex = Math.max(0, Math.min(items.length - 1, safeIndex + delta));
        if (newIndex !== safeIndex) {
            onChange(items[newIndex].value);
        }
    }, [items, safeIndex, onChange]);

    // Handle touch/drag
    const dragStartY = useRef(0);
    const dragStartOffset = useRef(0);
    const scrollOffsetRef = useRef(scrollOffset);
    
    // Keep ref in sync with state
    useEffect(() => {
        scrollOffsetRef.current = scrollOffset;
    }, [scrollOffset]);

    const handleDragStart = useCallback((e) => {
        e.stopPropagation();
        setIsDragging(true);
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        dragStartY.current = clientY;
        dragStartOffset.current = scrollOffsetRef.current;
    }, []);

    const handleDragMove = useCallback((e) => {
        e.stopPropagation();
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const deltaY = clientY - dragStartY.current;
        const newOffset = dragStartOffset.current + deltaY;
        setScrollOffset(newOffset);
    }, []);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        
        // Snap to nearest item
        const rawIndex = Math.round(-scrollOffsetRef.current / itemHeight);
        const newIndex = Math.max(0, Math.min(items.length - 1, rawIndex));
        onChange(items[newIndex].value);
        setScrollOffset(-newIndex * itemHeight);
    }, [items, onChange, itemHeight]);

    // Add non-passive touch event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        
        let isCurrentlyDragging = false;
        let startY = 0;
        let startOffset = 0;

        const touchStartHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isCurrentlyDragging = true;
            setIsDragging(true);
            startY = e.touches[0].clientY;
            startOffset = scrollOffsetRef.current;
        };

        const touchMoveHandler = (e) => {
            if (!isCurrentlyDragging) return;
            e.preventDefault();
            e.stopPropagation();
            const clientY = e.touches[0].clientY;
            const deltaY = clientY - startY;
            setScrollOffset(startOffset + deltaY);
        };

        const touchEndHandler = (e) => {
            if (!isCurrentlyDragging) return;
            e.stopPropagation();
            isCurrentlyDragging = false;
            setIsDragging(false);
            
            // Snap to nearest item
            const rawIndex = Math.round(-scrollOffsetRef.current / itemHeight);
            const newIndex = Math.max(0, Math.min(items.length - 1, rawIndex));
            onChange(items[newIndex].value);
            setScrollOffset(-newIndex * itemHeight);
        };

        // Use non-passive listeners to enable preventDefault on touch events
        container.addEventListener('touchstart', touchStartHandler, { passive: false });
        container.addEventListener('touchmove', touchMoveHandler, { passive: false });
        container.addEventListener('touchend', touchEndHandler, { passive: false });
        container.addEventListener('touchcancel', touchEndHandler, { passive: false });

        return () => {
            container.removeEventListener('touchstart', touchStartHandler);
            container.removeEventListener('touchmove', touchMoveHandler);
            container.removeEventListener('touchend', touchEndHandler);
            container.removeEventListener('touchcancel', touchEndHandler);
        };
    }, [items, onChange, itemHeight]);

    // Click on item to select
    const handleItemClick = (index) => {
        onChange(items[index].value);
    };

    return (
        <div className={`relative ${width} flex flex-col items-center`}>
            {/* Label */}
            {label && (
                <span className="text-[10px] font-inter text-primary/40 uppercase tracking-widest mb-2">
                    {label}
                </span>
            )}
            
            {/* Wheel container - with scroll containment to prevent page scroll */}
            <div 
                ref={containerRef}
                className="relative overflow-hidden cursor-ns-resize select-none"
                style={{ 
                    height: itemHeight * visibleItems,
                    touchAction: 'none', // Prevent browser handling of touch gestures
                    overscrollBehavior: 'contain', // Contain scroll to this element
                    WebkitOverflowScrolling: 'touch',
                }}
                onMouseDown={handleDragStart}
                onMouseMove={(e) => isDragging && handleDragMove(e)}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
            >
                {/* Gold selection frame lines */}
                <div 
                    className="absolute left-0 right-0 pointer-events-none z-10"
                    style={{ 
                        top: itemHeight * 2,
                        height: itemHeight,
                    }}
                >
                    {/* Top line */}
                    <div 
                        className="absolute top-0 left-2 right-2 h-px"
                        style={{ 
                            background: "linear-gradient(90deg, transparent, #D4AF37 20%, #D4AF37 80%, transparent)"
                        }}
                    />
                    {/* Bottom line */}
                    <div 
                        className="absolute bottom-0 left-2 right-2 h-px"
                        style={{ 
                            background: "linear-gradient(90deg, transparent, #D4AF37 20%, #D4AF37 80%, transparent)"
                        }}
                    />
                </div>

                {/* Gradient fade overlays */}
                <div 
                    className="absolute inset-x-0 top-0 h-16 pointer-events-none z-10"
                    style={{
                        background: "linear-gradient(to bottom, rgba(10,10,11,0.95) 0%, transparent 100%)"
                    }}
                />
                <div 
                    className="absolute inset-x-0 bottom-0 h-16 pointer-events-none z-10"
                    style={{
                        background: "linear-gradient(to top, rgba(10,10,11,0.95) 0%, transparent 100%)"
                    }}
                />

                {/* Items container */}
                <motion.div
                    className="flex flex-col items-center"
                    style={{
                        paddingTop: itemHeight * 2,
                        paddingBottom: itemHeight * 2,
                    }}
                    animate={{ y: scrollOffset }}
                    transition={isDragging ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 30 }}
                >
                    {items.map((item, index) => {
                        const isSelected = index === safeIndex;
                        const distance = Math.abs(index - safeIndex);
                        const opacity = isSelected ? 1 : Math.max(0.15, 1 - distance * 0.35);
                        const scale = isSelected ? 1 : Math.max(0.85, 1 - distance * 0.08);
                        
                        return (
                            <motion.div
                                key={item.value}
                                className="flex items-center justify-center cursor-pointer select-none"
                                style={{ 
                                    height: itemHeight,
                                    minHeight: itemHeight,
                                }}
                                onClick={() => handleItemClick(index)}
                                animate={{
                                    opacity,
                                    scale,
                                }}
                                transition={{ duration: 0.2 }}
                            >
                                <span 
                                    className="font-cinzel text-lg tracking-wider"
                                    translate="no"
                                    style={{
                                        color: isSelected ? "#FFD700" : "#D4AF37",
                                        textShadow: isSelected ? "0 0 15px rgba(255,215,0,0.5)" : "none"
                                    }}
                                >
                                    {item.label}
                                </span>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </div>
    );
};

/**
 * CelestialDatePicker - Three-column date tumbler (Month / Day / Year)
 */
export const CelestialDatePicker = ({ value, onChange }) => {
    // Parse current value - default to a reasonable birth year (1995)
    const parseDate = (dateStr) => {
        if (!dateStr) {
            return {
                month: 1,
                day: 1,
                year: 1995
            };
        }
        const [year, month, day] = dateStr.split('-').map(Number);
        return { month: month || 1, day: day || 1, year: year || 1995 };
    };

    const { month, day, year } = parseDate(value);
    const { t } = useLanguage();
    
    // Initialize with default value if empty - only run once on mount
    useEffect(() => {
        if (!value) {
            onChange('1995-01-01');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate options with translated month names
    const months = [
        { value: 1, label: t('months.jan') }, { value: 2, label: t('months.feb') }, { value: 3, label: t('months.mar') },
        { value: 4, label: t('months.apr') }, { value: 5, label: t('months.may') }, { value: 6, label: t('months.jun') },
        { value: 7, label: t('months.jul') }, { value: 8, label: t('months.aug') }, { value: 9, label: t('months.sep') },
        { value: 10, label: t('months.oct') }, { value: 11, label: t('months.nov') }, { value: 12, label: t('months.dec') }
    ];

    const days = Array.from({ length: 31 }, (_, i) => ({
        value: i + 1,
        label: String(i + 1).padStart(2, '0')
    }));

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => ({
        value: currentYear - i,
        label: String(currentYear - i)
    }));

    // Update handlers
    const handleMonthChange = (newMonth) => {
        const formatted = `${year}-${String(newMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onChange(formatted);
    };

    const handleDayChange = (newDay) => {
        const formatted = `${year}-${String(month).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
        onChange(formatted);
    };

    const handleYearChange = (newYear) => {
        const formatted = `${newYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onChange(formatted);
    };

    return (
        <div className="flex items-start justify-center gap-2 sm:gap-4">
            <CelestialWheel
                items={months}
                value={month}
                onChange={handleMonthChange}
                width="w-16 sm:w-20"
                label={t('ui.month')}
            />
            <CelestialWheel
                items={days}
                value={day}
                onChange={handleDayChange}
                width="w-14 sm:w-16"
                label={t('ui.day')}
            />
            <CelestialWheel
                items={years}
                value={year}
                onChange={handleYearChange}
                width="w-20 sm:w-24"
                label={t('ui.year')}
            />
        </div>
    );
};

/**
 * TimeOfDayIndicator - Visual feedback showing time of day with icon
 * Prevents midnight/noon confusion by showing contextual icons
 */
const TimeOfDayIndicator = ({ hour24 }) => {
    const { t } = useLanguage();
    
    // Determine time period and icon based on 24-hour time
    const getTimeOfDayInfo = (h) => {
        if (h >= 0 && h <= 5) {
            return { 
                icon: "🌑", 
                label: t('time_of_day.midnight'),
                description: t('time_of_day.start_of_day'),
                gradient: "from-indigo-900 to-slate-900"
            };
        } else if (h >= 6 && h <= 11) {
            return { 
                icon: "🌅", 
                label: t('time_of_day.morning'),
                description: t('time_of_day.before_noon'),
                gradient: "from-amber-600 to-orange-500"
            };
        } else if (h >= 12 && h <= 17) {
            return { 
                icon: "☀️", 
                label: t('time_of_day.afternoon'),
                description: t('time_of_day.after_noon'),
                gradient: "from-yellow-400 to-amber-500"
            };
        } else {
            return { 
                icon: "🌙", 
                label: t('time_of_day.evening'),
                description: t('time_of_day.end_of_day'),
                gradient: "from-purple-800 to-indigo-900"
            };
        }
    };

    const { icon, label, description } = getTimeOfDayInfo(hour24);

    return (
        <div className="flex flex-col items-center mt-4 mb-2">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/30 border border-primary/20">
                <span className="text-2xl">{icon}</span>
                <div className="flex flex-col">
                    <span className="text-xs font-inter text-primary/70 uppercase tracking-wider">
                        {label}
                    </span>
                    <span className="text-[10px] font-inter text-primary/40">
                        {description}
                    </span>
                </div>
            </div>
        </div>
    );
};

/**
 * CelestialTimePicker - Three-column time tumbler (Hour / Minute / AM-PM)
 * With Visual Time Verification to prevent midnight/noon confusion
 */
export const CelestialTimePicker = ({ value, onChange }) => {
    // Parse current value (expects HH:MM format in 24h)
    const parseTime = (timeStr) => {
        if (!timeStr) {
            return { hour: 12, minute: 0, period: "PM", hour24: 12 };
        }
        const [hourStr, minuteStr] = timeStr.split(':');
        // CRITICAL FIX: Don't use || for hour since 0 (midnight) is a valid value!
        const parsedHour = parseInt(hourStr);
        let hour24 = isNaN(parsedHour) ? 12 : parsedHour;
        const minute = parseInt(minuteStr) || 0;
        
        // 0-11 = AM, 12-23 = PM
        const period = hour24 >= 12 ? "PM" : "AM";
        let hour12 = hour24 % 12;
        if (hour12 === 0) hour12 = 12;
        
        return { hour: hour12, minute, period, hour24 };
    };

    const { hour, minute, period, hour24 } = parseTime(value);
    
    // Initialize with default value if empty - only run once on mount
    useEffect(() => {
        if (!value) {
            onChange('12:00');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate options
    const hours = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: String(i + 1).padStart(2, '0')
    }));

    const minutes = Array.from({ length: 60 }, (_, i) => ({
        value: i,
        label: String(i).padStart(2, '0')
    }));

    const periods = [
        { value: "AM", label: "AM" },
        { value: "PM", label: "PM" }
    ];

    // Convert to 24h format for output
    // CRITICAL: This ensures 12:xx AM = 00:xx (midnight) and 12:xx PM = 12:xx (noon)
    const formatOutput = (h, m, p) => {
        let hour24 = h;
        if (p === "PM" && h !== 12) hour24 = h + 12;  // 1-11 PM -> 13-23
        if (p === "AM" && h === 12) hour24 = 0;       // 12 AM -> 00 (MIDNIGHT)
        // Note: 12 PM stays as 12 (NOON)
        return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const handleHourChange = (newHour) => {
        onChange(formatOutput(newHour, minute, period));
    };

    const handleMinuteChange = (newMinute) => {
        onChange(formatOutput(hour, newMinute, period));
    };

    const handlePeriodChange = (newPeriod) => {
        onChange(formatOutput(hour, minute, newPeriod));
    };

    const { t } = useLanguage();
    
    return (
        <div className="flex flex-col items-center">
            {/* Time Picker Wheels */}
            <div className="flex items-start justify-center gap-2 sm:gap-4">
                <CelestialWheel
                    items={hours}
                    value={hour}
                    onChange={handleHourChange}
                    width="w-14 sm:w-16"
                    label={t('ui.hour')}
                />
                <div className="flex items-center justify-center h-[200px] text-primary/40 font-cinzel text-xl">
                    :
                </div>
                <CelestialWheel
                    items={minutes}
                    value={minute}
                    onChange={handleMinuteChange}
                    width="w-14 sm:w-16"
                    label={t('ui.min')}
                />
                <CelestialWheel
                    items={periods}
                    value={period}
                    onChange={handlePeriodChange}
                    width="w-14 sm:w-16"
                    label=""
                />
            </div>
            
            {/* Visual Time Verification Indicator */}
            <TimeOfDayIndicator hour24={hour24} />
        </div>
    );
};

export default { CelestialDatePicker, CelestialTimePicker };
